import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Notification} from 'app/layout/common/notifications/notifications.types';
import {map, Observable, ReplaySubject, switchMap, take, tap} from 'rxjs';
import {UserService} from '../../../core/user/user.service';

@Injectable({providedIn: 'root'})
export class NotificationsService
{
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
                private _userService: UserService)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications (CRITICAL + HIGH alerts only)
     * Now using /api/my-alerts with severity filtering (backend fixed as of Mar 4, 2026)
     */
    getAll(): Observable<{ data: Notification[] }>
    {
        // Backend expects multiple severity parameters: ?severity=critical&severity=high
        return this._httpClient.get<any>(
            '/api/my-alerts?severity=critical&severity=high&status=new&limit=10&sort=created_at&order=desc'
        ).pipe(
            map((response: any) => {
                // Get seen notification IDs from localStorage
                const seenIds = this.getSeenNotificationIds();
                
                const notifications: Notification[] = (response.data || []).map((alert: any) => {
                    const notificationId = `${alert.source}-${alert.id}`;
                    return {
                        id: notificationId,
                        icon: alert.severity === 'critical' 
                            ? 'heroicons_outline:shield-exclamation' 
                            : 'heroicons_outline:exclamation-triangle',
                        title: alert.title || alert.alert_type || 'Alerta de Seguridad',
                        description: alert.description || 'Nueva alerta detectada',
                        datetime: alert.created_at,
                        link: `/alerts`,  // Navigate to alerts dashboard
                        useRouter: true,
                        priority: alert.severity === 'critical' ? 'high' : 'medium',
                        is_read: seenIds.includes(notificationId),
                        honey_token_id: alert.security_event_id,
                        project_id: alert.project_id
                    };
                });

                this._notifications.next(notifications);
                
                return { data: notifications };
            }),
        );
    }

    /**
     * Get list of seen notification IDs from localStorage
     */
    private getSeenNotificationIds(): string[] {
        const stored = localStorage.getItem('seen_alert_notifications');
        return stored ? JSON.parse(stored) : [];
    }

    /**
     * Mark notification as seen in localStorage
     */
    private markNotificationAsSeen(notificationId: string): void {
        const seenIds = this.getSeenNotificationIds();
        if (!seenIds.includes(notificationId)) {
            seenIds.push(notificationId);
            // Keep only last 100 to avoid localStorage bloat
            const trimmedIds = seenIds.slice(-100);
            localStorage.setItem('seen_alert_notifications', JSON.stringify(trimmedIds));
        }
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            switchMap(notifications => this._httpClient.post<Notification>(`api/alerts/${notification.id}`, {notification}).pipe(
                map((newNotification) =>
                {
                    // Update the notifications with the new notification
                    this._notifications.next([...notifications, newNotification]);

                    // Return the new notification from observable
                    return newNotification;
                }),
            )),
        );
    }

    /**
     * Update the notification (mark as read/unread)
     * For security event notifications, we only update local state and localStorage
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        return this.notifications$.pipe(
            take(1),
            map(notifications => {
                // Find the index of the updated notification
                const index = notifications.findIndex(item => item.id === id);

                if (index !== -1) {
                    // Update the notification
                    notifications[index] = notification;

                    // If marked as read, store in localStorage
                    if (notification.is_read) {
                        this.markNotificationAsSeen(id);
                    }

                    // Update the notifications
                    this._notifications.next(notifications);
                }

                // Return the updated notification
                return notification;
            }),
        );
    }

    /**
     * Delete the notification from local state
     * For security event notifications, we only update local state
     *
     * @param id
     */
    delete(id: string): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            map(notifications => {
                // Find the index of the notification to delete
                const index = notifications.findIndex(item => item.id === id);

                if (index !== -1) {
                    // Delete the notification
                    notifications.splice(index, 1);

                    // Update the notifications
                    this._notifications.next(notifications);

                    // Mark as seen in localStorage (so it won't reappear)
                    this.markNotificationAsSeen(id);

                    return true;
                }

                return false;
            }),
        );
    }

    /**
     * Mark all notifications as read
     * Updates local state and marks all as seen in localStorage
     */
    markAllAsRead(): Observable<boolean>
    {
        return this.notifications$.pipe(
            take(1),
            map(notifications => {
                // Go through all notifications and set them as read
                notifications.forEach((notification, index) => {
                    notifications[index].is_read = true;
                    // Mark as seen in localStorage
                    this.markNotificationAsSeen(notification.id);
                });

                // Update the notifications
                this._notifications.next(notifications);

                return true;
            }),
        );
    }
}
